import { Component, OnDestroy, OnInit } from '@angular/core';
import { GlobalConstants } from 'app/meeting-manager/lib/global-constants';
import { Board } from 'app/meeting-manager/lib/models/board';
import { DataService } from 'app/core/services/data.service';
import { SnackbarService } from '../lib/services/snackbar.service';
import { Subscription } from 'rxjs';
import { BoardMember } from '../lib/models/board-member';
import { ActivatedRoute, Params } from '@angular/router';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Constants } from '../lib/constants';
import moment from 'moment';

@Component({
    selector: 'appc-boards',
    templateUrl: './boards.component.html',
    styleUrls: ['./boards.component.scss'],
    standalone: false,
})
export class BoardsComponent implements OnInit, OnDestroy {
  selectedBoard: Board | undefined;
  boardsMap: Map<number, Board> = new Map();
  boardDrawerType: string = '';
  boardDrawerOpen: boolean = false;

  selectedBoardTab: number = 0;
  boardSearchFilter: string = '';

  subscriptions: Subscription = new Subscription();

  BOARD_TERM_LIMIT_FORMAT = "MMM DD, YYYY";

  constructor (
    private globalConstants: GlobalConstants,
    private dataService: DataService,
    private snackbarService: SnackbarService,
    private route: ActivatedRoute,
    private breakpointObserver: BreakpointObserver,
    private constants: Constants
  ) {
  }
  
  ngOnInit() {
    this.route.params.subscribe((params: Params) => {
      this.boardDrawerOpen = false;
      this.selectedBoardTab = 0;

      const boardId = Number(params['boardId']);
      this.loadBoards(boardId);
      this.loadBreakpointForDrawers();
    })
  }

  

  ngOnDestroy () {
    this.subscriptions.unsubscribe();
  }

  get boards (): Board []  {
    return Array.from(this.boardsMap.values());
  }

  get filteredBoards (): Board []  {
    const { boards } = this;
    if (this.boardSearchFilter && this.boardSearchFilter !== '') {
      return boards.filter(board => board.name.toLowerCase().includes(this.boardSearchFilter.toLowerCase()));
    }

    return boards;
  }

  loadBoards(boardId: number) {
    let boardsUrl = this.globalConstants.baseUrl + '/boards/getBoards';

    // Load all boards even with a boardId in mind,
    // this will be used for the navigation.
    this.subscriptions.add(this.dataService.get(boardsUrl).subscribe({
      next: (res: any) => {
        this.boardsMap = new Map();
        let selectedBoardId = boardId;
        res.forEach((board: Board) => {
          if (Number.isNaN(selectedBoardId)) {
            // Fetch the first board if no board was selected in the first place
            selectedBoardId = board.boardId;
          }

          board.isBoardMembersLoading = false;
          this.boardsMap.set(board.boardId, board);
        })
        
        this.selectedBoard = this.boardsMap.get(selectedBoardId);
        this.loadBoardMembers();
      },
      error: (error: any) => {
        console.error("Error encountered fetching boards", error);
        this.snackbarService.showMessage("Error: Failed to fetch all boards.");
      }
    }));

    
  }

  loadBoardMembers () {
    if (!this.selectedBoard) {
      return;
    }

    const board = this.selectedBoard;
    board.isBoardMembersLoading = true;
    const boardMembersUrl = this.globalConstants.baseUrl + '/boards/getBoardMembersByBoardId/' + board.boardId;
    const profilePictureUrl = this.globalConstants.baseUrl + '/boards/getBoardProfilePicture/';
    this.subscriptions.add(this.dataService.get(boardMembersUrl).subscribe({
      next: (res: any) => {
        board.boardMembers = res.map((boardMember: BoardMember) => {
          if (boardMember.profilePictureDocId) {
            boardMember.profilePicture = profilePictureUrl + boardMember.profilePictureDocId;
          }
          
          if (!!boardMember.termStart && !!boardMember.termEnd) {
            const termStart = new Date(boardMember.termStart);
            const termEnd = new Date(boardMember.termEnd);
  
            boardMember.termRange = moment(termStart).format(this.BOARD_TERM_LIMIT_FORMAT) + ' - ' +  moment(termEnd).format(this.BOARD_TERM_LIMIT_FORMAT); 
          } 

          return boardMember;
        });

        board.isBoardMembersLoading = false;
      },
      error: (error: any) => {
        console.error("Error encountered fetching board members", error);
        this.snackbarService.showMessage("Error: Failed to fetch board members");
        board.isBoardMembersLoading = false;
      }
    }))
  }

  loadBreakpointForDrawers() {
    this.subscriptions.add(this.breakpointObserver.observe([
      "(max-width: 800px)"
    ]).subscribe((result: BreakpointState) => {
        this.boardDrawerType = result.matches ?
            this.constants.drawerTypes.dismissable :
            '';
    }));
  }

  get boardDrawerDismissable () {
    return this.boardDrawerType === this.constants.drawerTypes.dismissable;
  }
}
